import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import { useCallback, useRef, useState } from "react";

function ResizableImageComponent({ node, updateAttributes, selected, editor }: NodeViewProps) {
  const src = node.attrs.src as string;
  const alt = (node.attrs.alt as string) || "";
  const width = node.attrs.width as number | null;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  const startResize = useCallback((e: React.MouseEvent, direction: "right" | "left") => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startWidth = containerRef.current?.querySelector("img")?.offsetWidth ?? (width ?? 600);

    setIsResizing(true);

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.max(80, Math.round(direction === "right" ? startWidth + delta : startWidth - delta));
      updateAttributes({ width: newWidth });
    };

    const onMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [width, updateAttributes]);

  const displayWidth = width ? `${width}px` : "100%";
  const isEditable = editor.isEditable;

  return (
    <NodeViewWrapper
      className="resizable-image-wrapper"
      style={{ display: "block", margin: "1em 0", lineHeight: 0 }}
    >
      <div
        ref={containerRef}
        style={{
          position: "relative",
          display: "inline-block",
          maxWidth: "100%",
          cursor: isResizing ? "ew-resize" : "default",
          outline: selected && isEditable ? "2px solid hsl(var(--primary))" : "none",
          borderRadius: 4,
          userSelect: "none",
        }}
      >
        <img
          src={src}
          alt={alt || ""}
          style={{
            display: "block",
            width: displayWidth,
            maxWidth: "100%",
            height: "auto",
            borderRadius: 4,
          }}
          draggable={false}
        />

        {/* Handles de redimensionnement — visibles uniquement en mode édition sur sélection */}
        {selected && isEditable && (
          <>
            {/* Handle gauche */}
            <div
              onMouseDown={(e) => startResize(e, "left")}
              style={{
                position: "absolute",
                left: -5,
                top: "50%",
                transform: "translateY(-50%)",
                width: 10,
                height: 28,
                background: "hsl(var(--primary))",
                borderRadius: 4,
                cursor: "ew-resize",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div style={{ width: 2, height: 14, background: "rgba(255,255,255,0.7)", borderRadius: 1, margin: "0 1px" }} />
              <div style={{ width: 2, height: 14, background: "rgba(255,255,255,0.7)", borderRadius: 1 }} />
            </div>

            {/* Handle droit */}
            <div
              onMouseDown={(e) => startResize(e, "right")}
              style={{
                position: "absolute",
                right: -5,
                top: "50%",
                transform: "translateY(-50%)",
                width: 10,
                height: 28,
                background: "hsl(var(--primary))",
                borderRadius: 4,
                cursor: "ew-resize",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div style={{ width: 2, height: 14, background: "rgba(255,255,255,0.7)", borderRadius: 1, margin: "0 1px" }} />
              <div style={{ width: 2, height: 14, background: "rgba(255,255,255,0.7)", borderRadius: 1 }} />
            </div>

            {/* Badge largeur */}
            {width && (
              <div style={{
                position: "absolute",
                bottom: 6,
                right: 8,
                background: "rgba(0,0,0,0.6)",
                color: "#fff",
                fontSize: 10,
                padding: "1px 5px",
                borderRadius: 3,
                pointerEvents: "none",
                fontFamily: "monospace",
              }}>
                {width}px
              </div>
            )}
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
}

export const ResizableImage = Node.create({
  name: "image",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: "" },
      title: { default: null },
      width: {
        default: null,
        parseHTML: (el) => {
          // Restore from data-width (set by renderHTML), fallback to width attr, then style
          const dataWidth = el.getAttribute("data-width");
          if (dataWidth) return parseInt(dataWidth, 10);
          const widthAttr = el.getAttribute("width");
          if (widthAttr) return parseInt(widthAttr, 10);
          const styleWidth = el.style.width;
          if (styleWidth?.endsWith("px")) return parseInt(styleWidth, 10);
          return null;
        },
      },
      class: { default: "notebook-image" },
    };
  },

  parseHTML() {
    return [{ tag: "img[src]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs: Record<string, unknown> = { ...HTMLAttributes };
    if (attrs.width) {
      attrs.style = `width: ${attrs.width}px; max-width: 100%; height: auto;`;
      // Keep width as data attribute so parseHTML can restore it on reload
      attrs["data-width"] = attrs.width;
    }
    const { width, ...rest } = attrs;
    return ["img", mergeAttributes(rest)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageComponent);
  },
});
